
import React, { useState, useEffect } from 'react';
import { getProjectRecommendations } from '../services/geminiService';
import { AIRecommendation, WizardFormData } from '../types';
import { 
  BrainCircuit, 
  Loader2, 
  CheckCircle, 
  ChevronRight, 
  ArrowLeft, 
  Download, 
  Building2, 
  AlertTriangle,
  MapPin,
  ClipboardList,
  Target,
  FileSearch,
  Users
} from 'lucide-react';
import { Link } from 'react-router-dom';

const WizardPage: React.FC = () => {
  const [step, setStep] = useState<number>(1);
  const [formData, setFormData] = useState<WizardFormData>({
    address: '',
    projectType: '',
    budget: '',
    description: ''
  });
  const [results, setResults] = useState<AIRecommendation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    document.title = "Project Intelligence Wizard | PlanStack";
  }, []);

  const handleNext = () => setStep(s => s + 1);
  const handlePrev = () => setStep(s => s - 1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);
    setError(null);
    try {
      const recommendation = await getProjectRecommendations(formData);
      setResults(recommendation);
      setStep(4); // Result step
    } catch (err) {
      setError('The engine encountered an error analyzing your site. Please try a simpler description.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (isGenerating) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-8 text-center">
        <div className="relative w-32 h-32 mb-12">
          <Loader2 className="animate-spin text-blue-500 w-full h-full" strokeWidth={1} />
          <BrainCircuit className="absolute inset-0 m-auto text-blue-400" size={40} />
        </div>
        <h2 className="text-3xl font-black text-white mb-4 tracking-tight uppercase">Analysing National Frameworks</h2>
        <div className="max-w-xs w-full bg-white/5 h-1.5 rounded-full overflow-hidden mb-6 border border-white/10">
           <div className="h-full bg-blue-500 animate-[loading_4s_linear_infinite]" style={{width: '75%'}}></div>
        </div>
        <p className="text-slate-400 font-bold uppercase tracking-[0.2em] text-[10px] animate-pulse">Scanning Planning Zones • Calculating Fee Benchmarks • Drafting Strategy</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa] py-20">
      <div className="max-w-4xl mx-auto px-4">
        
        {step < 4 && (
          <div className="bg-white rounded-[2.5rem] shadow-2xl border border-slate-200 overflow-hidden">
            <div className="p-12 md:p-16">
              <div className="flex items-center gap-3 mb-10 text-blue-600">
                <BrainCircuit size={28} />
                <span className="font-black uppercase tracking-[0.3em] text-[11px]">Intelligence Wizard</span>
              </div>

              {step === 1 && (
                <div className="animate-fade-in">
                  <h1 className="text-4xl font-black text-slate-900 mb-4 tracking-tight leading-none">Where is your project <br/><span className="text-blue-600">located?</span></h1>
                  <p className="text-slate-500 font-medium mb-12">Address helps us identify local council requirements.</p>
                  <div className="relative">
                    <MapPin className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                    <input 
                      required
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl py-6 pl-16 pr-6 text-lg font-medium outline-none transition-all"
                      placeholder="e.g. 100 Collins St, Melbourne VIC"
                    />
                  </div>
                  <button onClick={handleNext} disabled={!formData.address} className="mt-12 bg-slate-900 text-white font-black px-12 py-5 rounded-2xl uppercase tracking-widest text-xs hover:bg-black transition-all shadow-xl disabled:opacity-50">Continue</button>
                </div>
              )}

              {step === 2 && (
                <div className="animate-fade-in">
                   <button onClick={handlePrev} className="text-slate-400 font-bold text-xs uppercase tracking-widest mb-8 flex items-center gap-2 hover:text-blue-600"><ArrowLeft size={14}/> Back</button>
                   <h1 className="text-4xl font-black text-slate-900 mb-4 tracking-tight leading-none">What are you <br/><span className="text-blue-600">building?</span></h1>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
                      {[
                        { id: 'Residential New Build', label: 'Single Dwelling' },
                        { id: 'Dual Occupancy / Units', label: 'Multi-Unit / Dual Occ' },
                        { id: 'Renovation / Extension', label: 'Renovations' },
                        { id: 'Commercial / Industrial', label: 'Commercial' },
                      ].map(type => (
                        <button 
                          key={type.id}
                          onClick={() => { setFormData({...formData, projectType: type.id}); handleNext(); }}
                          className={`p-6 rounded-2xl border-2 text-left transition-all ${formData.projectType === type.id ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 bg-slate-50 hover:border-blue-200'}`}
                        >
                          <div className="font-black text-slate-900 uppercase tracking-tight text-sm mb-1">{type.label}</div>
                          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{type.id}</div>
                        </button>
                      ))}
                   </div>
                </div>
              )}

              {step === 3 && (
                <form onSubmit={handleSubmit} className="animate-fade-in">
                   <button onClick={handlePrev} className="text-slate-400 font-bold text-xs uppercase tracking-widest mb-8 flex items-center gap-2 hover:text-blue-600"><ArrowLeft size={14}/> Back</button>
                   <h1 className="text-4xl font-black text-slate-900 mb-4 tracking-tight leading-none">The Project <br/><span className="text-blue-600">Brief</span></h1>
                   <div className="mt-10 space-y-8">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 ml-1">Estimated Budget (Optional)</label>
                        <input 
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-xl py-4 px-6 font-medium outline-none transition"
                          placeholder="e.g. $600,000"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 ml-1">Goals & Site Notes</label>
                        <textarea 
                          required
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={6}
                          className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-xl py-4 px-6 font-medium outline-none transition"
                          placeholder="Describe the site constraints (slope, trees) and your development goals..."
                        ></textarea>
                      </div>
                   </div>
                   {error && <div className="mt-6 text-red-600 text-sm font-bold flex items-center gap-2 bg-red-50 p-4 rounded-xl border border-red-100"><AlertTriangle size={16}/> {error}</div>}
                   <button type="submit" className="mt-12 w-full bg-blue-600 text-white font-black py-6 rounded-2xl uppercase tracking-widest text-xs hover:bg-blue-700 transition shadow-xl flex items-center justify-center gap-3">
                      Generate Project Intelligence <ChevronRight size={18}/>
                   </button>
                </form>
              )}
            </div>
          </div>
        )}

        {step === 4 && results && (
          <div className="animate-fade-in pb-32">
             <div className="bg-slate-900 rounded-[3rem] shadow-2xl overflow-hidden border border-slate-800 mb-12">
                <div className="p-12 md:p-16">
                   <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-16">
                      <div>
                        <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-4">Project Intelligence Report</div>
                        <h1 className="text-4xl font-black text-white uppercase tracking-tight leading-none">{formData.address}</h1>
                      </div>
                      <div className="bg-white/5 border border-white/10 p-6 rounded-3xl backdrop-blur-xl text-center">
                         <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Fee Benchmark</div>
                         <div className="text-2xl font-black text-white">{results.estimatedFeeRange}</div>
                      </div>
                   </div>

                   <div className="grid md:grid-cols-2 gap-16">
                      <div className="space-y-12">
                         <div>
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-2"><Target size={14}/> Executive Summary</h3>
                            <p className="text-slate-300 font-medium leading-relaxed italic border-l-4 border-blue-500 pl-6 py-2">{results.summary}</p>
                         </div>
                         <div>
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-2"><FileSearch size={14}/> Required Reports</h3>
                            <div className="grid grid-cols-1 gap-3">
                               {results.requiredReports.map(report => (
                                 <div key={report} className="bg-white/5 border border-white/10 p-4 rounded-2xl text-white text-sm font-bold flex items-center gap-3">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div> {report}
                                 </div>
                               ))}
                            </div>
                         </div>
                      </div>
                      <div className="space-y-12">
                         <div>
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-2"><Users size={14}/> Consultant Team</h3>
                            <div className="space-y-4">
                               {results.coreRoles.map(role => (
                                 <div key={role} className="flex items-center justify-between bg-white/5 border border-white/10 p-5 rounded-2xl hover:bg-white/10 transition group">
                                    <div>
                                       <h4 className="text-white font-black text-sm uppercase tracking-tight">{role}</h4>
                                       <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Core Specialist</p>
                                    </div>
                                    <Link to={`/directory?category=${encodeURIComponent(role)}`} className="bg-blue-600 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition"><ChevronRight size={16}/></Link>
                                 </div>
                               ))}
                               {results.additionalRoles && results.additionalRoles.map(role => (
                                 <div key={role} className="flex items-center justify-between bg-white/5 border border-white/10 p-5 rounded-2xl hover:bg-white/10 transition group">
                                    <div>
                                       <h4 className="text-white font-black text-sm uppercase tracking-tight">{role}</h4>
                                       <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Support Consultant</p>
                                    </div>
                                    <Link to={`/directory?category=${encodeURIComponent(role)}`} className="bg-blue-600 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition"><ChevronRight size={16}/></Link>
                                 </div>
                               ))}
                            </div>
                         </div>
                      </div>
                   </div>
                   
                   <div className="mt-16 pt-16 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-8">
                      <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest max-w-sm">Disclaimer: This report is generated by AI based on standard planning frameworks and serves as a roadmap only. Always verify with local council.</p>
                      <button onClick={() => window.print()} className="bg-white text-slate-900 font-black px-12 py-5 rounded-2xl uppercase tracking-widest text-xs flex items-center gap-3 hover:bg-slate-100 transition shadow-xl"><Download size={18}/> Export Blueprint</button>
                   </div>
                </div>
             </div>

             <div className="text-center">
                <button onClick={() => setStep(1)} className="text-slate-400 font-bold uppercase tracking-widest text-[10px] flex items-center gap-2 mx-auto hover:text-blue-600 transition">
                   <ArrowLeft size={14}/> Start New Analysis
                </button>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WizardPage;
